import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  CalendarDays,
  Check,
  Copy,
  Eye,
  EyeOff,
  KeyRound,
  LucideIcon,
  Mail,
  RefreshCw,
  Star,
  UserCheck,
  UserCog,
} from 'lucide-react';

import { ConfirmationOverlay } from 'components/feedback/ConfirmationOverlay/ConfirmationOverlay';
import { toasterService } from 'components/feedback/Toaster/toasterService';
import { useDialog } from 'hooks/useDialog';
import { useAbp } from 'core/hooks/useAbp';
import { AbpPolicies } from 'core/constants/AbpPolicies';
import { impersonateUser } from 'features/impersonation/impersonationService';
import { DisplayTypeEnum } from 'api/files';
import { GenericColors } from 'api/tenant';
import {
  AccessMember,
  deleteUserAccount,
  resetUserPassword,
} from 'features/access/api/personneMoraleAccessApi';

interface MemberDetailModalProps {
  member: AccessMember;
  /** Rafraîchit la liste des membres après une suppression. */
  onSuccess: () => void;
}

const InfoRow = ({
  icon: Icon,
  label,
  children,
}: {
  icon: LucideIcon;
  label: string;
  children: React.ReactNode;
}) => (
  <div className="flex items-center gap-3 py-2.5">
    <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-gray-50 text-gray-500">
      <Icon size={16} />
    </div>
    <div className="flex min-w-0 flex-col">
      <span className="text-[11px] font-medium uppercase tracking-wide text-gray-400">
        {label}
      </span>
      <span className="text-sm text-gray-900">{children}</span>
    </div>
  </div>
);

export const CollaboratorDetailModal: React.FC<MemberDetailModalProps> = ({
  member,
  onSuccess,
}) => {
  const { setDialogActions, closeDialog } = useDialog();

  const [tempPassword, setTempPassword] = useState<string | null>(null);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [copied, setCopied] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showImpersonateConfirm, setShowImpersonateConfirm] = useState(false);
  const [impersonateLoading, setImpersonateLoading] = useState(false);

  const { hasPermission } = useAbp();
  const canImpersonate =
    hasPermission(AbpPolicies.IMPERSONATE_USER) && member.isActive;

  const handleImpersonate = useCallback(async () => {
    setImpersonateLoading(true);
    try {
      const res = await impersonateUser(member.userId);
      if (!res.isSuccess) {
        toasterService.showToast({
          message: res.errorMessage ?? "Échec de l'impersonation.",
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
        setShowImpersonateConfirm(false);
      }
      // Succès : impersonateUser recharge l'application.
    } finally {
      setImpersonateLoading(false);
    }
  }, [member.userId]);

  useEffect(() => {
    setDialogActions({
      onSubmit: closeDialog,
      submitLabel: 'COMMON.ACTIONS.CLOSE',
      onDelete: () => setShowDeleteConfirm(true),
      deleteLabel: 'Supprimer le compte',
      deleteDisabled: deleteLoading,
    });
  }, [setDialogActions, closeDialog, deleteLoading]);

  const handleResetPassword = useCallback(async () => {
    setResetLoading(true);
    try {
      const res = await resetUserPassword(member.accessId);
      if (!res.isSuccess || !res.data?.temporaryPassword) {
        toasterService.showToast({
          message: res.errorMessage ?? 'Échec de la réinitialisation.',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
        return;
      }
      setTempPassword(res.data.temporaryPassword);
      setPasswordVisible(true);
      setCopied(false);
    } finally {
      setResetLoading(false);
    }
  }, [member.accessId]);

  const handleCopy = useCallback(async () => {
    if (!tempPassword) return;
    await navigator.clipboard.writeText(tempPassword);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [tempPassword]);

  const handleDelete = useCallback(async () => {
    setDeleteLoading(true);
    try {
      const res = await deleteUserAccount(member.accessId);
      if (!res.isSuccess) {
        toasterService.showToast({
          message: res.errorMessage ?? 'Échec de la suppression.',
          displayType: DisplayTypeEnum.Toaster,
          color: GenericColors.Red,
        });
        return;
      }
      toasterService.showToast({
        message: `Le compte de ${member.displayName} a été supprimé.`,
        displayType: DisplayTypeEnum.Toaster,
        color: GenericColors.Green,
      });
      onSuccess();
      closeDialog();
    } finally {
      setDeleteLoading(false);
    }
  }, [member.accessId, member.displayName, onSuccess, closeDialog]);

  const formattedDate = useMemo(() => {
    try {
      return new Date(member.dateAdhesion).toLocaleDateString('fr-FR');
    } catch {
      return '—';
    }
  }, [member.dateAdhesion]);

  return (
    <div className="flex flex-col gap-1">
      <InfoRow icon={UserCog} label="Nom">
        {member.displayName}
      </InfoRow>
      <InfoRow icon={Mail} label="Email">
        {member.email}
      </InfoRow>
      <InfoRow icon={Star} label="Gérant">
        <span className="inline-flex items-center gap-1.5">
          {member.isOwner ? 'Gérant du cabinet' : 'Collaborateur'}
          {member.isPrincipal && (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
              ★ Principal
            </span>
          )}
        </span>
      </InfoRow>
      <InfoRow icon={CalendarDays} label="Date d'adhésion">
        {formattedDate}
      </InfoRow>

      <div className="mt-4 rounded-xl border border-gray-200 p-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <KeyRound size={16} className="text-gray-500" />
          Mot de passe
        </div>

        {!tempPassword ? (
          <>
            <p className="mt-1 text-xs text-gray-500">
              Le mot de passe actuel n'est pas récupérable. La réinitialisation génère
              un nouveau mot de passe temporaire et déconnecte l'utilisateur.
            </p>
            <button
              type="button"
              onClick={handleResetPassword}
              disabled={resetLoading}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-primary px-3.5 py-2 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50"
            >
              <RefreshCw size={14} className={resetLoading ? 'animate-spin' : ''} />
              {resetLoading ? 'Réinitialisation…' : 'Réinitialiser le mot de passe'}
            </button>
          </>
        ) : (
          <>
            <p className="mt-1 text-xs text-amber-700">
              Nouveau mot de passe temporaire — affiché une seule fois, communiquez-le
              au collaborateur.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <code className="flex-1 truncate rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-sm text-gray-900">
                {passwordVisible ? tempPassword : '•'.repeat(tempPassword.length)}
              </code>
              <button
                type="button"
                onClick={() => setPasswordVisible((v) => !v)}
                title={passwordVisible ? 'Masquer' : 'Afficher'}
                className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
              >
                {passwordVisible ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
              <button
                type="button"
                onClick={handleCopy}
                title="Copier"
                className="rounded-lg border border-gray-200 p-2 text-gray-500 hover:bg-gray-50"
              >
                {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
              </button>
            </div>
          </>
        )}
      </div>

      {canImpersonate && (
        <div className="mt-3 rounded-xl border border-gray-200 p-4">
          <div className="flex items-center gap-2 text-sm font-semibold text-gray-800">
            <UserCheck size={16} className="text-gray-500" />
            Support
          </div>
          <p className="mt-1 text-xs text-gray-500">
            Ouvre l'Espace Pro sous l'identité de ce collaborateur (portails et
            droits identiques aux siens). L'action est tracée côté serveur.
          </p>
          <button
            type="button"
            onClick={() => setShowImpersonateConfirm(true)}
            className="mt-3 inline-flex items-center gap-2 rounded-lg border border-primary px-3.5 py-2 text-sm font-medium text-primary hover:bg-primary/5"
          >
            <UserCheck size={14} />
            Se connecter en tant que {member.displayName}
          </button>
        </div>
      )}

      <ConfirmationOverlay
        isOpen={showImpersonateConfirm}
        onClose={() => setShowImpersonateConfirm(false)}
        onConfirm={handleImpersonate}
        title="Se connecter en tant que"
        description={`Votre session va être remplacée par celle de ${member.displayName}. Vous pourrez revenir à votre compte via le bandeau en haut de page.`}
        confirmLabel="Se connecter"
        cancelLabel="COMMON.CANCEL"
        isLoading={impersonateLoading}
      />

      <ConfirmationOverlay
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={handleDelete}
        title="Supprimer définitivement le compte"
        description={`Le compte de ${member.displayName} sera anonymisé et désactivé. Cette action est irréversible. Tapez EFFACER pour confirmer.`}
        requireTypedConfirmation="EFFACER"
        confirmLabel="Supprimer définitivement"
        cancelLabel="COMMON.CANCEL"
        variant="danger"
        isLoading={deleteLoading}
      />
    </div>
  );
};
